import { ClassKey } from "@const/class.constants";
import { API_URL } from "src/data/Api";
import { AllType, isDevicesTree, RoutePath } from "src/data/Routes";

/**
 * Pure core of the menu get-path URL. Kept free of GLOB and of the domain registry — every
 * menu lookup arrives through PathUrlDeps — so it stays unit-testable without the app graph.
 * See pathUrl.ts for the wired-up entry point callers use.
 */

/** Route segments addressing the menu get-path endpoint. */
export interface PathUrlContext {
  clsPath: RoutePath;
  allType: AllType;
  /** itemId of the server group the item lives in, without a '/' prefix. */
  groupId: string;
  /** '/'-prefixed type or device segment, or null. */
  typeOrDevicePath: string;
  /** '/'-prefixed item name plus the query string. */
  itemSearch: string;
}

/** Lookups the URL depends on, injected to keep the builder pure. */
export interface PathUrlDeps {
  /** Storage devices-tree segments carry the device title — maps it back to its hwType path. */
  storageDeviceHwTypePath(typeOrDevicePath: string): string;
  /** URL of the domain owning the class route, when it has its own endpoint. */
  navDomainPathUrl(clsPath: RoutePath, itemSearch: string): string;
}

/** Item segment of the group configuration page, served by the LAN handler for most classes. */
export const GROUP_CONFIGURATION = '/groupconfiguration';

/** Query parameter carrying the server group id. */
export const SGROUP_ID_PARAM = 'sgroup_id';

/** Classes serving group configuration themselves instead of through the LAN handler. */
const OWN_GROUP_CONFIGURATION = ['/' + ClassKey.VIRTUALIZATION, '/' + ClassKey.SERVERSP] as string[];

/**
 * The hw_type segment the endpoint expects, which is not always the URL's own type segment.
 * Note isDevicesTree() covers both the device and the group tree, and that both are storage
 * only — the device and group allTypes are never emitted for another class.
 */
export function resolveHwTypePath(ctx: PathUrlContext, deps: PathUrlDeps): string {
  const { clsPath, allType, typeOrDevicePath, itemSearch } = ctx;
  if (isDevicesTree(allType)) {
    return deps.storageDeviceHwTypePath(typeOrDevicePath);
  }
  if (itemSearch === GROUP_CONFIGURATION && !OWN_GROUP_CONFIGURATION.includes(clsPath)) {
    return RoutePath.LAN_CFG;
  }
  return typeOrDevicePath;
}

/** Appends a query parameter, opening the query string when the item search carries none. */
function addParam(url: string, name: string, value: string): string {
  return url + (url.includes('?') ? '&' : '?') + name + '=' + value;
}

/** Builds the menu URL resolving an item's path. */
export function buildPathUrl(ctx: PathUrlContext, deps: PathUrlDeps): string {
  const { clsPath, groupId, itemSearch } = ctx;
  const domainUrl = deps.navDomainPathUrl(clsPath, itemSearch);
  if (domainUrl) return domainUrl;
  if (!groupId) {
    return API_URL.MENU + RoutePath.GET_PATH + (resolveHwTypePath(ctx, deps) || '') + itemSearch;
  }
  // Items of a server group are addressed by group, so 'sgroup' takes the hw_type segment.
  const url = API_URL.MENU + RoutePath.GET_PATH + RoutePath.SGROUP + itemSearch;
  return itemSearch.includes('sgroup') ? url : addParam(url, SGROUP_ID_PARAM, groupId);
}
